'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useState, useCallback, type SyntheticEvent } from 'react'
import '../../sass/landing/LogoCloud.scss'

interface Props {
  logos: string[]
}

const LogoCloud = ({ logos }: Props) => {
  const t = useTranslations('LandingV2.logoCloud')
  const [widths, setWidths] = useState<Record<string, number>>({})

  const handleLoad = useCallback(
    (logo: string) => (e: SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget
      if (img.naturalWidth && img.naturalHeight) {
        const renderedHeight = img.offsetHeight || 50
        const targetWidth = (img.naturalWidth / img.naturalHeight) * renderedHeight
        setWidths(prev =>
          prev[logo] === targetWidth ? prev : { ...prev, [logo]: targetWidth }
        )
      }
    },
    []
  )

  if (logos.length === 0) return null

  const splitIntoRows = (arr: string[], rows: number) => {
    const perRow = Math.ceil(arr.length / rows)
    return Array.from({ length: rows }, (_, i) => arr.slice(i * perRow, (i + 1) * perRow))
  }

  const [row1, row2, row3] = splitIntoRows(logos, 3)
  const tracks = [
    { logos: row1, key: 'row1', reverse: false },
    { logos: row2, key: 'row2', reverse: true },
    { logos: row3, key: 'row3', reverse: false }
  ]

  return (
    <section className='logo-cloud'>
      <p className='logo-cloud__label'>{t('label')}</p>

      {tracks.map(({ logos: row, key, reverse }) => (
        <div key={key} className='logo-cloud__marquee' aria-hidden='true'>
          <div
            className={`logo-cloud__track${reverse ? ' logo-cloud__track--reverse' : ''}`}
          >
            {[0, 1].map((copy) => (
              <div key={copy} className='logo-cloud__set'>
                {row.map((logo, i) => (
                  <Image
                    key={`${key}-${copy}-${i}`}
                    className='logo-cloud__logo'
                    src={`/customers-logos/${logo}`}
                    alt=''
                    width={200}
                    height={50}
                    loading='eager'
                    onLoad={handleLoad(logo)}
                    style={widths[logo] ? { width: `${widths[logo]}px`, height: 'auto' } : undefined}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  )
}

export default LogoCloud
